import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { NavLink } from "react-router";
import { twJoin } from "tailwind-merge";

import { NumericBadge } from "@/design-system/NumericBadge";
import { twVariants } from "@/helpers/tw-resolve";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import type { RuntimeFloatingPanelAttributes } from "@/ui-adapters/RuntimeFloatingPanel";
import { RuntimeFloatingPortal } from "@/ui-adapters/RuntimeFloatingPanel";

import { ActivityDot } from "../ActivityDot/ActivityDot";
import { sidebarFloatingElementAnimation, sidebarItemLabelAnimation } from "./animations";
import { type SidebarBaseItemType } from "./types";
import { useSidebarCollapsedTooltip } from "./useSidebarCollapsedTooltip";

interface SidebarItemProps extends Omit<SidebarBaseItemType, "key" | "type"> {
  isLabelVisible?: boolean;
  tabIndex?: number;
}

export function SidebarItem({
  label,
  to,
  exact,
  icon,
  "data-testid": dataTestId,
  newEventCount,
  isLabelVisible,
  isNewEventAvailable,
  onClick,
}: SidebarItemProps): React.ReactNode {
  const { showTooltip, hideTooltip, referenceRef, panelProps, isTooltipVisible, isCollapsed } =
    useSidebarCollapsedTooltip();
  const isDesktop = useIsDesktop();

  return (
    <>
      <li className="w-full">
        <NavLink
          className={({ isActive }) => sidebarItemVariants({ isActive, isCollapsed })}
          end={exact}
          to={to}
          data-testid={dataTestId}
          onMouseEnter={isCollapsed ? showTooltip : undefined}
          onMouseLeave={isCollapsed ? hideTooltip : undefined}
          ref={referenceRef}
          // Required for screenreader when sidebar is collapsed
          aria-label={label}
          {...{ onClick }}
        >
          <span className={twJoin("flex min-w-0 items-center gap-space-sm", isCollapsed ? undefined : "truncate")}>
            {icon && (
              <span className="relative flex size-icon-inline shrink-0 items-center justify-center [&_svg]:size-icon-inline">
                {icon}
                {/* Notification indicator */}
                {isNewEventAvailable && (
                  <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3">
                    <ActivityDot />
                  </span>
                )}
              </span>
            )}
            <AnimatePresence>
              {(!isCollapsed || isLabelVisible) && (
                <motion.span
                  className="block truncate font-medium"
                  variants={sidebarItemLabelAnimation}
                  initial={isDesktop && !isLabelVisible ? "hidden" : "visible"}
                  animate="visible"
                  exit="hidden"
                >
                  {label}
                </motion.span>
              )}
            </AnimatePresence>
          </span>

          {!isCollapsed && isNewEventAvailable && !!newEventCount && newEventCount > 0 && (
            <NumericBadge count={newEventCount} variant="filled" />
          )}
        </NavLink>
      </li>
      {/* Floating tooltip */}
      <AnimatePresence>
        {!isLabelVisible && isTooltipVisible && (
          <RuntimeFloatingPortal>
            <SidebarItemFloatingTooltip label={label} floating={panelProps} isVisible={isTooltipVisible} />
          </RuntimeFloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}

export interface SidebarItemFloatingTooltipProps {
  label: string;
  floating: RuntimeFloatingPanelAttributes;
  isVisible: boolean;
}

export function SidebarItemFloatingTooltip({ label, floating }: SidebarItemFloatingTooltipProps): React.ReactNode {
  return (
    <motion.span
      className={twJoin(
        "pointer-events-none z-backdrop ml-space-sm rounded-lg bg-background-default-inverse px-tooltip-inline py-tooltip-block text-body-emphasis whitespace-nowrap text-text-default-inverse",
      )}
      variants={sidebarFloatingElementAnimation}
      initial="hidden"
      animate="visible"
      exit="hidden"
      {...floating}
    >
      {label}
    </motion.span>
  );
}

export const sidebarItemVariants = twVariants({
  base: [
    "relative mx-auto flex min-h-control-md w-full items-center justify-between overflow-hidden rounded-lg p-space-sm",
    "text-body-emphasis focus:outline-hidden focus-visible:bg-action-subtle-primary-hover",
  ],
  variants: {
    isCollapsed: {
      true: "max-w-control-md justify-center",
      false: "justify-between",
    },
    isActive: {
      true: "bg-action-soft-primary text-button-tone-primary hover:bg-action-soft-primary focus-visible:bg-action-soft-primary active:bg-action-soft-primary",
      false: "text-button-tone-primary hover:bg-action-subtle-primary-hover active:bg-action-soft-primary",
    },
  },
  defaultVariants: {
    isCollapsed: false,
    isActive: false,
  },
});
